/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience builder prompting guide", description = "How to prompt the Agent in the audience builder to draft, refine, explore, and troubleshoot audiences with natural language.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience builder prompting guide",
        description: "How to prompt the Agent in the audience builder to draft, refine, explore, and troubleshoot audiences with natural language.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Write effective prompts",
          "depth": 0,
          "id": "write-effective-prompts"
        }, {
          "value": "Start broad, then refine",
          "depth": 0,
          "id": "start-broad-then-refine"
        }, {
          "value": "Ask what data is available",
          "depth": 0,
          "id": "ask-what-data-is-available"
        }, {
          "value": "Narrow the search",
          "depth": 1,
          "id": "narrow-the-search"
        }, {
          "value": "If the agent can't find a field",
          "depth": 1,
          "id": "if-the-agent-cant-find-a-field"
        }, {
          "value": "Refine an existing audience",
          "depth": 0,
          "id": "refine-an-existing-audience"
        }, {
          "value": "Understand an audience",
          "depth": 0,
          "id": "understand-an-audience"
        }, {
          "value": "Troubleshoot with the agent",
          "depth": 0,
          "id": "troubleshoot-with-the-agent"
        }, {
          "value": "Audience is smaller than expected",
          "depth": 1,
          "id": "audience-is-smaller-than-expected"
        }, {
          "value": "The agent can't find the data you need",
          "depth": 1,
          "id": "the-agent-cant-find-the-data-you-need"
        }, {
          "value": "The audience changed unexpectedly",
          "depth": 1,
          "id": "the-audience-changed-unexpectedly"
        }, {
          "value": "Analysis is slow or times out",
          "depth": 1,
          "id": "analysis-is-slow-or-times-out"
        }, {
          "value": "Improve results with better schema context",
          "depth": 0,
          "id": "improve-results-with-better-schema-context"
        }, {
          "value": "Agent in the audience builder vs. Agents",
          "depth": 0,
          "id": "agent-in-the-audience-builder-vs-agents"
        }, {
          "value": "AI and data privacy",
          "depth": 0,
          "id": "ai-and-data-privacy"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, growth teams, campaign managers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A configured ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "schema"
              }), ". Open an existing audience or start a new one."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The agent in the audience builder drafts audience conditions from a plain-language request. This guide covers how to prompt it well: writing a first draft, refining it, exploring your data, and troubleshooting."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "The agent works alongside the audience builder. You describe what you want in natural language, and it drafts or updates the conditions for you. You can also ask it to explore the fields and events in your schema, explain existing filters, or help troubleshoot unexpected results."
      }), "\n", _jsxs(_components.p, {
        children: ["This guide focuses on the prompts themselves. For how to open the agent, apply its proposed changes, and undo them, see ", _jsx(_components.a, {
          href: "/customer-studio/usage#build-with-the-agent",
          children: "Build an audience"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/agents/build-audience-natural-language.png",
          alt: "Audience builder with the agent drafting conditions from a natural-language request"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you're starting with an open-ended question rather than a specific audience, use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " instead. For example, use Agents to investigate which customers are most valuable or which segment is underperforming, then create or refine the resulting audience in Customer Studio."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "write-effective-prompts",
        children: "Write effective prompts"
      }), "\n", _jsx(_components.p, {
        children: "You don't need a special prompt format, but specific requests produce more predictable audience logic. Include details such as:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "who"
          }), " you want to target"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "what behavior or attribute"
          }), " matters"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "when"
          }), " it needs to have happened"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "thresholds"
          }), " such as counts or values"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "exclusions"
          }), " that should not qualify"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Instead of ", _jsx(_components.code, {
          children: "Find my best customers."
        }), ", try:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Create an audience of customers who completed at least 3 orders in the last 90 days and have an average order value over $100."
        })
      }), "\n", _jsx(_components.p, {
        children: "The first prompt leaves the agent to infer what \"best\" means. The second names the behavior, the window, and the thresholds, so the draft is closer to what you intended."
      }), "\n", _jsx(_components.p, {
        children: "After the agent drafts an audience, review the definition before saving:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Check that it used the fields, events, and traits you expected."
        }), "\n", _jsx(_components.li, {
          children: "Confirm the time windows and thresholds."
        }), "\n", _jsx(_components.li, {
          children: "Check any exclusions."
        }), "\n", _jsx(_components.li, {
          children: "Preview the qualifying members."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If something isn't right, continue the conversation instead of starting over:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Exclude customers who already purchased in the last 7 days."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Change the cart window from 30 days to 14 days."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Keep the purchase condition, but remove the country filter."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "start-broad-then-refine",
        children: "Start broad, then refine"
      }), "\n", _jsx(_components.p, {
        children: "You can build the audience over several messages, checking the result at each step."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Step"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Start"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Build an audience of active customers."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add detail"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Limit it to customers who logged in at least 3 times in 30 days."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Exclude"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Exclude anyone who already upgraded."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Inspect"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "How many customers match each condition?"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Adjust"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Change the login threshold to at least once."
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If you don't want the agent to modify part of the audience, say so explicitly:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Add a filter for email engagement, but don't change the purchase filter."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "ask-what-data-is-available",
        children: "Ask what data is available"
      }), "\n", _jsx(_components.p, {
        children: "If you're not sure which fields, traits, events, or relationships you can use, ask the agent before building the audience. For example:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "What fields can I use to filter by customer tenure?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "What events are available for audience filtering?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "What properties are available on the Order Completed event?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "What traits can I use?"
        })
      }), "\n", _jsx(_components.p, {
        children: "This is especially useful when you're working with a schema you didn't configure yourself."
      }), "\n", _jsx(_components.h3, {
        id: "narrow-the-search",
        children: "Narrow the search"
      }), "\n", _jsx(_components.p, {
        children: "If the agent returns too much information, narrow the request:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "What fields relate to email engagement?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Which fields contain dates or timestamps?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "List fields with \"subscription\" in the name."
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "if-the-agent-cant-find-a-field",
        children: "If the agent can't find a field"
      }), "\n", _jsx(_components.p, {
        children: "The field may use a different name, live on a related model, or not be exposed in the Customer Studio schema. Ask the agent to inspect related models or search for similar names."
      }), "\n", _jsxs(_components.p, {
        children: ["If the field still isn't available, ask your data team to review the ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "schema"
        }), ". Clear model names, field descriptions, event labels, and trait descriptions make it easier for both marketers and the agent to understand the data."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "refine-an-existing-audience",
        children: "Refine an existing audience"
      }), "\n", _jsx(_components.p, {
        children: "Use follow-up prompts to change the current audience without rebuilding it."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Goal"
            }), _jsx(_components.th, {
              children: "Example prompt"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add a condition"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Include only customers who opened an email in the last 14 days."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Remove a condition"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Remove the country filter."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add an exclusion"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Exclude anyone who unsubscribed in the last 60 days."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Change a window"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Change the purchase window from 90 days to 30 days."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Change a threshold"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Require at least 2 purchases instead of 3."
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Explain a filter"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "What does the third filter do?"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To ground a prompt in a specific condition, send that filter to the agent from the builder: open the filter's ", _jsx(_components.strong, {
          children: "..."
        }), " menu and select ", _jsx(_components.strong, {
          children: "Add filter to chat"
        }), ". See ", _jsx(_components.a, {
          href: "/customer-studio/usage#build-with-the-agent",
          children: "Build an audience"
        }), " for the steps."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "understand-an-audience",
        children: "Understand an audience"
      }), "\n", _jsx(_components.p, {
        children: "You can ask the agent to explain the current audience in plain language. For example:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Summarize this audience in one sentence."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Which condition excludes the most customers?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Explain why this audience is smaller than expected."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Break down this audience by region."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For visual composition, overlap, and breakdown analysis, use ", _jsx(_components.a, {
          href: "/customer-studio/insights",
          children: "Audience insights"
        }), ". For broader questions that span multiple audiences or campaigns, use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshoot-with-the-agent",
        children: "Troubleshoot with the agent"
      }), "\n", _jsx(_components.p, {
        children: "The agent can help you investigate audience logic, but it doesn't replace checks on the underlying data or schema."
      }), "\n", _jsx(_components.h3, {
        id: "audience-is-smaller-than-expected",
        children: "Audience is smaller than expected"
      }), "\n", _jsxs(_components.p, {
        children: ["Ask ", _jsx(_components.code, {
          children: "Which condition removes the most customers?"
        }), " or ", _jsx(_components.code, {
          children: "How many customers match each filter individually?"
        }), ", then check for:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "filters that become too restrictive when combined"
        }), "\n", _jsx(_components.li, {
          children: "time windows that are narrower than intended"
        }), "\n", _jsx(_components.li, {
          children: "missing or null values"
        }), "\n", _jsx(_components.li, {
          children: "unexpected relationship behavior"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/customer-studio/audience-filters",
          children: "Audience filters"
        }), " for filter semantics and null behavior."]
      }), "\n", _jsx(_components.h3, {
        id: "the-agent-cant-find-the-data-you-need",
        children: "The agent can't find the data you need"
      }), "\n", _jsx(_components.p, {
        children: "Ask it to search for similar field names, inspect related models, or list available traits or events. If the field isn't exposed in the schema, your data team may need to add or describe it."
      }), "\n", _jsx(_components.p, {
        children: "The agent works within the current audience's parent model and its related models. It can't query across separate parent models or pull data from other audiences, so a field on an unrelated model isn't reachable from here."
      }), "\n", _jsxs(_components.p, {
        children: ["Some column types can't be used in filters directly. Ask ", _jsx(_components.code, {
          children: "What data type is the [column name] column?"
        }), " If the type is unsupported, create a ", _jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "trait"
        }), " that transforms the column into a filterable value, or ask your data team to transform it upstream."]
      }), "\n", _jsx(_components.h3, {
        id: "the-audience-changed-unexpectedly",
        children: "The audience changed unexpectedly"
      }), "\n", _jsx(_components.p, {
        children: "Ask the agent to explain the current definition, then check whether source data changed, a trait changed, a schema relationship changed, or a rolling time window caused customers to enter or leave. The agent can help interpret the audience, but it doesn't replace the underlying source-of-truth checks."
      }), "\n", _jsx(_components.h3, {
        id: "analysis-is-slow-or-times-out",
        children: "Analysis is slow or times out"
      }), "\n", _jsxs(_components.p, {
        children: ["If the agent's analysis queries take too long, narrow what it evaluates. For example: ", _jsx(_components.code, {
          children: "Analyze the first three conditions only."
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "improve-results-with-better-schema-context",
        children: "Improve results with better schema context"
      }), "\n", _jsx(_components.p, {
        children: "The agent uses the Customer Studio schema to understand what data is available for audience building. Clear schema metadata improves how reliably it maps your request to the right fields. Use:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "business-readable model names"
        }), "\n", _jsx(_components.li, {
          children: "clear field descriptions"
        }), "\n", _jsx(_components.li, {
          children: "descriptive event labels"
        }), "\n", _jsx(_components.li, {
          children: "understandable trait names"
        }), "\n", _jsx(_components.li, {
          children: "accurate relationships"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, a trait named ", _jsx(_components.code, {
          children: "90-day purchase value"
        }), " with a description that explains the time window and included orders is easier to interpret than ", _jsx(_components.code, {
          children: "ltv_90d_v2"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "Schema"
        }), " and ", _jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "Traits"
        }), " for how to improve the data context available to audience builders and the agent."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "agent-in-the-audience-builder-vs-agents",
        children: "Agent in the audience builder vs. Agents"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the agent in the audience builder when you're working on a specific audience. Use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " when your starting point is a broader question or analysis."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Start with..."
            }), _jsx(_components.th, {
              children: "Use..."
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "An audience idea, template, or existing definition to build or change"
            }), _jsx(_components.td, {
              children: "The agent in the audience builder"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A question about who to target, or campaign or performance analysis"
            }), _jsx(_components.td, {
              children: "Agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Audience-specific filter troubleshooting"
            }), _jsx(_components.td, {
              children: "The agent in the audience builder"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cross-audience analysis, reports, or recurring work"
            }), _jsx(_components.td, {
              children: "Agents"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The two experiences don't share conversation history. If analysis in Agents leads you to an audience worth targeting, create the audience in Customer Studio and continue refining its definition there."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "ai-and-data-privacy",
        children: "AI and data privacy"
      }), "\n", _jsxs(_components.p, {
        children: ["When you use the agent, Hightouch sends relevant audience context — attributes, behavior signals, and schema metadata — to the AI model to generate a response. This data is not retained after the response, is not used to train or fine-tune models made available to other customers, and is governed by Hightouch's ", _jsx(_components.a, {
          href: "https://hightouch.com/ai-addendum",
          children: "AI Terms of Service Addendum"
        }), ". Your conversations with the agent are private to you. Other team members can't see your audience-specific chats."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " for the end-to-end audience workflow."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/audience-filters",
            children: "Audience filters"
          }), " for filter types, nested conditions, and operator behavior."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/traits",
            children: "Traits"
          }), " for reusable calculated values."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/insights",
            children: "Audience insights"
          }), " to validate audience composition and overlap."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
